import React, { useState, useEffect, useRef, useCallback } from "react";
import { useStyles as useRootStyles } from "../styles";
import { useStyles } from "./styles";
import { Grid, Accordion, AccordionSummary, Typography, AccordionDetails, FormControlLabel, Switch, TextField, MenuItem, Button, Drawer, Box, IconButton, Tooltip } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import EditIcon from "@material-ui/icons/Edit";
import useFormHandlers from "../useFormHandlers";
import { useSettingDispatchChange } from "../index";
import * as Yup from "yup";
import { OFFLINE_TICKET_EXIT_FUNCTION, OFFLINE_TICKET_EXIT_FUNCTION_VALUES } from "../../../../constants";
import RichTextEditor from "../../../MUIRichTextEditor";
import {Controller, useForm} from "react-hook-form";
import clsx from "clsx";
import PropTypes from "prop-types";
import OfflineHelpButtonEditor from "./OfflineHelpButtonEditor";
import useEntitySettings from "../../../../hooks/useEntitySettings";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlags";

// Constants for offline help button options
const OFFLINE_HELP_BUTTON_OPTIONS = {
  DISPLAY_HELP_MESSAGE: 0,
  NO_HELP_BUTTON: 1
};

const OFFLINE_HELP_BUTTON_LABELS = {
  [OFFLINE_HELP_BUTTON_OPTIONS.DISPLAY_HELP_MESSAGE]: "Display Help Message",
  [OFFLINE_HELP_BUTTON_OPTIONS.NO_HELP_BUTTON]: "No Help Button"
};

export const offlineFunctionalitySchema = Yup.object().shape({
    offlinefunctionalityenabled: Yup.bool(),
    exitofflinemessage: Yup.string()
        .min(2, "Message must be at least 2 characters")
        .max(50, "Message must be at most 50 characters")
        .required("Message is required"),
    offlinehelpbuttonfunction: Yup.number(),
    offlinehelpbuttonmessage: Yup.string(),
    offlinehelpbuttonqrcode: Yup.string().max(2000, "QR code text must be at most 2000 characters"),
  });

const FacilityOfflineFunctionalitySettingsForm = ({entityID, entityType, settingValues}) => {
    const rootClasses = useRootStyles();
    const classes = useStyles();
    const { getSettingValue } = useEntitySettings();
    const isContractBufferingEnabled = useFeatureFlag('Offline.Phase 1.Contract Buffering');
    
    const [offlineFunctionalityExpanded, setOfflineFunctionalityExpanded] = useState(true);
    const [isDeviceMessageChanged, setIsDeviceMessageChanged] = useState(false);
    const [helpButtonDrawerOpen, setHelpButtonDrawerOpen] = useState(false);
    const [formError, setFormError] = useState(null);
    const [offlineHelpButtonSettings, setOfflineHelpButtonSettings] = useState({
        function: OFFLINE_HELP_BUTTON_OPTIONS.DISPLAY_HELP_MESSAGE,
        message: "",
        qrCode: ""
    });
    const settingsLoadedRef = useRef(false);
    const userToggledOfflineRef = useRef(false);
    
    // Memoized function to load settings
    const loadOfflineHelpButtonSettings = useCallback(() => {
        if (entityID && !settingsLoadedRef.current) {
            // Try both with and without 'setting.' prefix
            let functionValue = getSettingValue(entityID, "offlinehelpbuttonfunction");
            if (functionValue === undefined) {
                functionValue = getSettingValue(entityID, "setting.offlinehelpbuttonfunction");
            }
            
            let messageValue = getSettingValue(entityID, "offlinehelpbuttonmessage");
            if (messageValue === undefined) {
                messageValue = getSettingValue(entityID, "setting.offlinehelpbuttonmessage");
            }
            
            let qrCodeValue = getSettingValue(entityID, "offlinehelpbuttonqrcode");
            if (qrCodeValue === undefined) {
                qrCodeValue = getSettingValue(entityID, "setting.offlinehelpbuttonqrcode");
            }
            
            const settings = {
                function: functionValue !== undefined ? parseInt(functionValue) : OFFLINE_HELP_BUTTON_OPTIONS.DISPLAY_HELP_MESSAGE,
                message: messageValue || "",
                qrCode: qrCodeValue || ""
            };
            
            setOfflineHelpButtonSettings(settings);
            settingsLoadedRef.current = true;
            
            return settings;
        }
        
        return null;
    }, [entityID, getSettingValue]);
    

    // Global error handler
    const handleGlobalError = (error) => {
        console.error('Global error in FacilityOfflineFunctionalitySettingsForm:', error);
        setFormError(`An error occurred: ${error.message || 'Unknown error'}`);
    };

    // Set up global error handler
    useEffect(() => {
        const originalErrorHandler = window.onerror;
        const originalUnhandledRejectionHandler = window.onunhandledrejection;

        window.onerror = (message, source, lineno, colno, error) => {
            handleGlobalError(error || new Error(message));
            if (originalErrorHandler) {
                return originalErrorHandler(message, source, lineno, colno, error);
            }
        };

        window.onunhandledrejection = (event) => {
            handleGlobalError(event.reason);
            if (originalUnhandledRejectionHandler) {
                return originalUnhandledRejectionHandler(event);
            }
        };

        return () => {
            window.onerror = originalErrorHandler;
            window.onunhandledrejection = originalUnhandledRejectionHandler;
        };
    }, []);

    const { control, formState: { errors }, setValue, getValues, watch} = useForm({
        defaultValues: {
            offlinefunctionalityenabled: false,
            offlineticketexitfunction: 0,
            exitofflinemessage: "",
            offlinehelpbuttonfunction: OFFLINE_HELP_BUTTON_OPTIONS.DISPLAY_HELP_MESSAGE,
            offlinehelpbuttonmessage: "",
            offlinehelpbuttonqrcode: "",
            enableaccesscredentialbuffering: false
        },
        validationSchema: offlineFunctionalitySchema,
    });

    // Load all offline help button settings once when the form loads
    useEffect(() => {
        const settings = loadOfflineHelpButtonSettings();
        if (settings) {
            // Also update the form values
            setValue("offlinehelpbuttonfunction", settings.function);
            setValue("offlinehelpbuttonmessage", settings.message);
            setValue("offlinehelpbuttonqrcode", settings.qrCode);
        }
    }, [entityID, loadOfflineHelpButtonSettings, setValue]);

    useEffect(() => {
        if (settingValues) {
            // Only set values if user hasn't manually toggled this setting
            if (!userToggledOfflineRef.current) {
                setValue("offlinefunctionalityenabled", settingValues.offlinefunctionalityenabled || false);
            }
            
            setValue("offlineticketexitfunction", settingValues.offlineticketexitfunction || 0);
            setValue("exitofflinemessage", settingValues.exitofflinemessage || "");
            setValue("enableaccesscredentialbuffering", settingValues.enableaccesscredentialbuffering || false);
        }
    }, [settingValues, setValue]);
    const isOfflineFunctionalityEnabled = watch("offlinefunctionalityenabled");
    const selectedHelpButtonOption = watch("offlinehelpbuttonfunction");



    const { handleToggle, handleBulkSettingUpdate, handleInputSave, handleSelectChange } = useFormHandlers(setValue, settingValues, entityID, entityType);
    const { dispatchSetting } = useSettingDispatchChange();

    const toggleOfflineFunctionalityExpanded = () => {
        setOfflineFunctionalityExpanded(!offlineFunctionalityExpanded);
    }

    const handleRichTextSave = (text) => {
        setValue("exitofflinemessage", text)
        canEnableSaveButton();
    };

    const handleDeviceMessageSave = () => {
        handleInputSave('exitofflinemessage', getValues('exitofflinemessage'))
    } 

    const canEnableSaveButton = () => {
        const defaultMessage = settingValues.exitofflinemessage;
        const updatedMessage = getValues('exitofflinemessage');
        
        // Safely parse HTML content
        let updatedTextContent = '';
        try {
            if (updatedMessage && typeof updatedMessage === 'string') {
                const parser = new DOMParser();
                const doc = parser.parseFromString(updatedMessage, 'text/html');
                const bodyElement = doc.body.firstChild;
                updatedTextContent = bodyElement ? bodyElement.textContent.trim() : '';
            }
        } catch (error) {
            console.warn('Error parsing HTML content:', error);
            updatedTextContent = updatedMessage || '';
        }

        if((defaultMessage === updatedMessage) || !updatedTextContent || !getValues("offlinefunctionalityenabled")) {
            setIsDeviceMessageChanged(false);
        } else {
            setIsDeviceMessageChanged(true);
        }
    }

    const updateBulkSettings = async (event) => {
        const enableOfflineFunctionality = event.target.checked;
        const settingName = event.target.name;
        
        setValue("offlinefunctionalityenabled", enableOfflineFunctionality);
        userToggledOfflineRef.current = true; // Mark that user has toggled this setting
      
        if (enableOfflineFunctionality) {
          // When enabling, use bulk update to set all related settings
          const targetNameList = ["offlinefunctionalityenabled", "offlineticketexitfunction", "exitofflinemessage", "offlinehelpbuttonfunction", "offlinehelpbuttonmessage", "offlinehelpbuttonqrcode", "enableaccesscredentialbuffering"];
          const values = { ...settingValues, [settingName]: enableOfflineFunctionality };
          if (!(await handleBulkSettingUpdate(targetNameList, values))) {
            setValue("offlinefunctionalityenabled", !enableOfflineFunctionality);
          }
        } else {
          const result = await dispatchSetting(entityID, settingName, enableOfflineFunctionality);
          
          if (!result) {
            setValue("offlinefunctionalityenabled", !enableOfflineFunctionality);
          }
        }
    };

    const updateCredentialBufferingSettings = async (event) => {
        const enableAccessCredentialBuffering = event.target.checked;
  
        setValue("enableaccesscredentialbuffering", enableAccessCredentialBuffering);
      
        if (enableAccessCredentialBuffering) {
          const targetNameList = ["enableaccesscredentialbuffering"];
          const values = { ...settingValues, [event.target.name]: enableAccessCredentialBuffering };
          if (!(await handleBulkSettingUpdate(targetNameList, values))) {
            setValue("enableaccesscredentialbuffering", !enableAccessCredentialBuffering);
          }
        } else {
          handleToggle({ enableaccesscredentialbuffering: !enableAccessCredentialBuffering }, {}, event);
        }
    };

    const openHelpButtonEditor = () => {
        try {
            // Use stored settings instead of trying to read from database
            const currentMessage = offlineHelpButtonSettings.message;
            const currentQRCode = offlineHelpButtonSettings.qrCode;
            
            // Only open if we have the entityID
            if (!entityID) {
                return;
            }
            
            setHelpButtonDrawerOpen(true);
        } catch (error) {
            console.error('Error opening help button editor:', error);
        }
    };

    const closeHelpButtonEditor = () => {
        try {
            setHelpButtonDrawerOpen(false);
        } catch (error) {
            console.error('Error closing help button editor:', error);
        }
    };

    const handleHelpButtonSave = (helpMessage, qrCodeText) => {
        try {
            
            setValue("offlinehelpbuttonmessage", helpMessage);
            setValue("offlinehelpbuttonqrcode", qrCodeText);
            
            setOfflineHelpButtonSettings(prev => ({
                ...prev,
                message: helpMessage,
                qrCode: qrCodeText
            }));
            
            handleInputSave('offlinehelpbuttonmessage', helpMessage);
            handleInputSave('offlinehelpbuttonqrcode', qrCodeText);
            
            closeHelpButtonEditor();
        } catch (error) {
            console.error('Error saving help button settings:', error);
        }
    };

    try {
        return(
            <Grid className={rootClasses.panelRoot} container>
            {formError && (
                <Grid item xs={12}>
                    <Typography color="error" variant="body1" style={{ padding: '16px', backgroundColor: '#ffebee' }}>
                        {formError}
                    </Typography>
                </Grid>
            )}
            
            <Accordion
                data-tag="offline-functionality"
                className={rootClasses.fullWidthAccordion}
                TransitionProps={{ unmountOnExit: true }}
                expanded={offlineFunctionalityExpanded}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon/>}
                    onClick={toggleOfflineFunctionalityExpanded}
                    data-testid="offline-settings-accordion"
                >
                    <Typography className={rootClasses.heading}>
                        Offline Functionality
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Grid item xs>
                                <FormControlLabel
                                    role="enable-offline-functionality-toggle"
                                    label="Enable Offline Functionality"
                                    control={
                                        <Controller
                                            name="offlinefunctionalityenabled"
                                            control={control}
                                            render={({ field }) => (
                                                <Switch
                                                    {...field}
                                                    color="primary"
                                                    onChange={event => updateBulkSettings(event)}
                                                    checked={field.value}
                                                    data-checked={field.value}
                                                    inputProps={{"aria-label": "offlinefunctionalityenabledswitch","data-testid": "offlinefunctionalityenabled-switch"}}
                                                />
                                            )}
                                        />
                                    }
                                />
                            </Grid>
                            <Grid item xs className={classes.ticketExitFunctionDropdown}>
                                <Controller
                                    name="offlineticketexitfunction"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label={<Typography>Offline Ticket Exit Function</Typography>}
                                            select
                                            variant="outlined"
                                            inputProps={{
                                                ["data-testid"]: "offlineticketexitfunction-select",
                                            }}
                                            value={field.value}
                                            name="offlineticketexitfunction"
                                            onChange={(e, selected) =>
                                                handleSelectChange(errors, e, selected)
                                            }
                                            disabled={!isOfflineFunctionalityEnabled}
                                        >
                                            {Object.keys(OFFLINE_TICKET_EXIT_FUNCTION).map((key) => (
                                                <MenuItem key={key} value={OFFLINE_TICKET_EXIT_FUNCTION_VALUES[key]}>
                                                    {OFFLINE_TICKET_EXIT_FUNCTION[key]}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    )}
                                />
                            </Grid>
                            <Grid item xs className={classes.ticketExitFunctionDropdown}>
                                <Controller
                                    name="offlinehelpbuttonfunction"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label={<Typography>Offline Help Button</Typography>}
                                            select
                                            variant="outlined"
                                            inputProps={{
                                                ["data-testid"]: "offlinehelpbutton-select",
                                            }}
                                            value={field.value}
                                            name="offlinehelpbuttonfunction"
                                            onChange={(e, selected) => handleSelectChange(errors, e, selected)}
                                            disabled={!isOfflineFunctionalityEnabled}
                                        >
                                            <MenuItem value={OFFLINE_HELP_BUTTON_OPTIONS.DISPLAY_HELP_MESSAGE}>
                                                <Tooltip
                                                    title="If someone presses the Help button while the device is offline, a user configured message will be displayed."
                                                    placement="right"
                                                    arrow
                                                    disableInteractive
                                                >
                                                    <span>
                                                        {OFFLINE_HELP_BUTTON_LABELS[OFFLINE_HELP_BUTTON_OPTIONS.DISPLAY_HELP_MESSAGE]}
                                                    </span>
                                                </Tooltip>
                                            </MenuItem>

                                            <MenuItem value={OFFLINE_HELP_BUTTON_OPTIONS.NO_HELP_BUTTON}>
                                                <Tooltip
                                                    title="Help button is not displayed on the device when offline."
                                                    placement="right"
                                                    arrow
                                                    disableInteractive
                                                >
                                                    <span>
                                                        {OFFLINE_HELP_BUTTON_LABELS[OFFLINE_HELP_BUTTON_OPTIONS.NO_HELP_BUTTON]}
                                                    </span>
                                                </Tooltip>
                                            </MenuItem>
                                        </TextField>
                                    )}
                                />
                                {selectedHelpButtonOption === OFFLINE_HELP_BUTTON_OPTIONS.DISPLAY_HELP_MESSAGE && (
                                    <Tooltip title="View editor for help button message.">
                                        <span>
                                            <IconButton
                                                onClick={openHelpButtonEditor}
                                                className={classes.editButton}
                                                disabled={!isOfflineFunctionalityEnabled}
                                                data-testid="offlinehelpbutton-edit"
                                            >
                                                <EditIcon data-testid="EditIcon"/>
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                            </Grid>
                            { isContractBufferingEnabled && (
                            <Grid item xs className={classes.enableAccessCredentialBuffering} >
                               <Tooltip title="Stores access holder prox/avi credentials while the device is offline and uploads the data to the cloud once communication is restored." arrow>
                               <FormControlLabel
                                    role="enable-access-credential-buffering-toggle"
                                    label="Enable Access Credential Buffering"
                                    control={
                                        <Controller
                                            name="enableaccesscredentialbuffering"
                                            control={control}
                                            render={({ field }) => (
                                                <Switch
                                                    {...field}
                                                    color="primary"
                                                    onChange={event => updateCredentialBufferingSettings(event)}
                                                    checked={field.value}
                                                    data-checked={field.value}
                                                    inputProps={{"aria-label": "enableaccesscredentialbufferingswitch","data-testid": "enableaccesscredentialbuffering-switch"}}
                                                    disabled={!isOfflineFunctionalityEnabled}
                                                />
                                            )}
                                        />
                                    }
                                /> 
                                </Tooltip>
                            </Grid>
                            )}
                        </Grid>
                        {(Number(getValues("offlineticketexitfunction")) === OFFLINE_TICKET_EXIT_FUNCTION_VALUES.DisplayOfflineMessage) && (
                            <Grid item xs={12} sm={6}>
                                    <Grid item xs className={classes.deviceMessageGrid}>
                                        <div className={clsx("offlineDeviceMessageEditor",
                                            classes.imgContainer,
                                        )}>
                                            <RichTextEditor
                                                disabled={!getValues("offlinefunctionalityenabled")}
                                                label="Enter device message"
                                                onChange={handleRichTextSave}
                                                content={settingValues.exitofflinemessage}
                                                customEditorStyles={{}}
                                                data-testid="exitofflinemessage-editor"
                                                aria-label="Enter device message"
                                            />
                                        </div>
                                    </Grid>
                                    <Grid item xs className={classes.deviceMessageGrid}>
                                        <Button
                                            className={clsx(["btn-submit", classes.submitBtn])}
                                            color="primary"
                                            type="submit"
                                            variant="contained"
                                            onClick={handleDeviceMessageSave}
                                            disabled={!isDeviceMessageChanged}
                                            data-testid="exitofflinemessage-save"
                                        >
                                            Save
                                        </Button>
                                    </Grid>
                            </Grid>
                        )}
                    </Grid>
                </AccordionDetails>
            </Accordion>

            {(() => {
                try {
                    const currentMessage = offlineHelpButtonSettings.message;
                    const currentQRCode = offlineHelpButtonSettings.qrCode;
                    
                    
                    if (!entityID) {
                        return null;
                    }
                    
                    return (
                        <Drawer
                            anchor="right"
                            open={helpButtonDrawerOpen}
                            onClose={closeHelpButtonEditor}
                            classes={{
                                paper: classes.helpButtonDrawerPaper
                            }}
                        >
                            <OfflineHelpButtonEditor
                                onSave={handleHelpButtonSave}
                                onCancel={closeHelpButtonEditor}
                                initialMessage={currentMessage}
                                initialQRCode={currentQRCode}
                                entityID={entityID}
                            />
                        </Drawer>
                    );
                } catch (error) {
                    console.error('Error rendering drawer:', error);
                    return null;
                }
            })()}
        </Grid>
        );
    } catch (error) {
        console.error('Critical error in FacilityOfflineFunctionalitySettingsForm:', error);
        return (
            <Grid container>
                <Grid item xs={12}>
                    <Typography color="error" variant="h6" style={{ padding: '16px', backgroundColor: '#ffebee' }}>
                        Critical Error: The form failed to load. Please refresh the page.
                    </Typography>
                    <Typography variant="body2" style={{ padding: '16px' }}>
                        Error details: {error.message || 'Unknown error'}
                    </Typography>
                </Grid>
            </Grid>
        );
    }
}

FacilityOfflineFunctionalitySettingsForm.defaultProps = { 
    entityID: "",
    entityType: "",
    settingValues: {}
}

FacilityOfflineFunctionalitySettingsForm.propTypes = { 
    entityID: PropTypes.string, 
    entityType: PropTypes.string,
    settingValues: PropTypes.any
}

export default FacilityOfflineFunctionalitySettingsForm;